import { AxiosError } from 'axios';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import { useLocationData } from './useGetLocationData';

type CreateIndividualFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

export interface IndividualCustomerFormDetails {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  addressLine1: string;
  addressLine2: string;
  barangay: string;
  city: string;
  province: string;
  zipCode: string;
  typeOfId: string;
  idNumber: string;
  profession: string;
  isAMechanic: boolean;
  workCompanyName: string;
  mechanicType: 'SelfEmployed' | 'CompanyEmployed';
  membershipCardNumber: string;
}

const CreateIndividualForm = forwardRef<CreateIndividualFormHandle>(
  (_, ref) => {
    const [individualDetails, setIndividualDetails] =
      useState<IndividualCustomerFormDetails>({
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: '',
        addressLine1: '',
        addressLine2: '',
        barangay: '',
        city: '',
        province: '',
        zipCode: '',
        typeOfId: '',
        idNumber: '',
        profession: '',
        isAMechanic: false,
        workCompanyName: '',
        mechanicType: 'SelfEmployed',
        membershipCardNumber: ''
      });
    const { provinces, cities, barangays, fetchCities, fetchBarangays } =
      useLocationData();

    const handleIndividualFormChange = <
      K extends keyof IndividualCustomerFormDetails
    >(
      field: K,
      value: IndividualCustomerFormDetails[K]
    ) => {
      setIndividualDetails(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const createIndividualFormSchema = yup.object().shape({
      firstName: yup.string().required('First name is required'),
      lastName: yup.string().required('Last name is required'),
      email: yup.string().email('Invalid email format'),
      contactNumber: yup
        .string()
        .matches(/^[0-9]+$/, 'Mobile number must be numeric')
        .required('Mobile number is required'),
      addressLine1: yup.string(),
      addressLine2: yup.string(),
      barangay: yup.string(),
      city: yup.string().required('City is required'),
      province: yup.string().required('Province is required'),
      zipCode: yup.string(),
      idNumber: yup.string(),
      profession: yup.string(),
      workCompanyName: yup.string().required('Company name is required'),
      membershipCardNumber: yup.string()
    });

    const handleSubmit = async () => {
      try {
        await createIndividualFormSchema.validate(individualDetails, {
          abortEarly: false
        });
        await axiosInstance.post(
          `/api/v1/customers/individuals`,
          individualDetails
        );
        toast.success('Customer Created', { ...toastConfig, autoClose: 2000 });
        return true;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          error.inner.forEach(err => {
            toast.error(err.message, { ...toastConfig, autoClose: 3000 });
          });
        } else if (error instanceof AxiosError) {
          toast.error(error.response?.data.errorMessage, {
            ...toastConfig,
            autoClose: 3000
          });
        }
      }
      return false;
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit
    }));

    return (
      <Form className="mb-3">
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.firstName}
              onChange={e =>
                handleIndividualFormChange('firstName', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.lastName}
              onChange={e =>
                handleIndividualFormChange('lastName', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.email}
              onChange={e =>
                handleIndividualFormChange('email', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Contact Number</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.contactNumber}
              onChange={e =>
                handleIndividualFormChange('contactNumber', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.addressLine1}
              onChange={e =>
                handleIndividualFormChange('addressLine1', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.addressLine2}
              onChange={e =>
                handleIndividualFormChange('addressLine2', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Province</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.province}
              onChange={e => {
                const selectedProvinceCode = e.target.value;
                const selectedProvince = provinces.find(
                  province => province.name === selectedProvinceCode
                );

                if (selectedProvince) {
                  handleIndividualFormChange('province', selectedProvince.name);
                }

                fetchCities(selectedProvince?.code as string);
              }}
            >
              <option value="">Select City</option>
              {provinces.map((province, index) => (
                <option key={index} value={province.name}>
                  {province.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>City</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.city}
              onChange={e => {
                const selectedCity = e.target.value;
                const selectedCityDistrictCode = cities.find(
                  city => city.name === selectedCity
                );
                handleIndividualFormChange('city', e.target.value);
                console.log(cities);
                fetchBarangays(
                  selectedCityDistrictCode?.districtCode as string,
                  selectedCityDistrictCode?.code as string
                );
              }}
            >
              <option value="">Select City</option>
              {cities.map((city, index) => (
                <option key={index} value={city.name}>
                  {city.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Barangay</Form.Label>
            <Form.Control
              as="select"
              value={individualDetails.barangay}
              onChange={e =>
                handleIndividualFormChange('barangay', e.target.value)
              }
            >
              <option value="">Select Barangay</option>
              {barangays.map((barangay, index) => (
                <option key={index} value={barangay.name}>
                  {barangay.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Zip Code</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.zipCode}
              onChange={e =>
                handleIndividualFormChange('zipCode', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Type Of Id</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.typeOfId}
              onChange={e =>
                handleIndividualFormChange('typeOfId', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>ID Number</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.idNumber}
              onChange={e =>
                handleIndividualFormChange('idNumber', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Profession</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.profession}
              onChange={e =>
                handleIndividualFormChange('profession', e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Work Company Name</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.workCompanyName}
              onChange={e =>
                handleIndividualFormChange('workCompanyName', e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Is a Mechanic</Form.Label>
            <Form.Select
              value={individualDetails.isAMechanic ? 'true' : 'false'}
              onChange={e =>
                handleIndividualFormChange(
                  'isAMechanic',
                  e.target.value === 'true'
                )
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          </Form.Group>
          {individualDetails.isAMechanic && (
            <Form.Group as={Col}>
              <Form.Label>Mechanic Type</Form.Label>
              <Form.Select
                value={individualDetails.mechanicType}
                onChange={e =>
                  handleIndividualFormChange(
                    'mechanicType',
                    e.target.value as 'SelfEmployed' | 'CompanyEmployed'
                  )
                }
              >
                <option value="">Select Type</option>
                <option value="SelfEmployed">Self Employed</option>
                <option value="CompanyEmployed">Company Employed</option>
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group as={Col}>
            <Form.Label>Membership Card Number (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={individualDetails.membershipCardNumber}
              onChange={e =>
                handleIndividualFormChange('membershipCardNumber', e.target.value)
              }
            />
          </Form.Group>
        </Row>
      </Form>
    );
  }
);

export default CreateIndividualForm;